import { memo, useState, useCallback, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { useTenantUsage } from "@/hooks/useTenantUsage";
import { listProducts, createProduct, updateProduct, updateProductStatus, isProductError } from "@/infrastructure/product/productService";
import { isPlanLimitError } from "@/infrastructure/errors/planLimitError";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import { listCategories } from "@/infrastructure/category/categoryService";
import type { Product, ProductErrorCode, ProductStatus, TypeUnit, ProductMeta, Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { Tooltip } from "@/design-system";
import { ProductFormModal } from "./components/ProductFormModal";
import { ProductImportModal } from "./components/ProductImportModal";
import { ImportProgressBar } from "./components/ImportJobBanner";
import { PlanLimitModal } from "../components/PlanLimitModal";
import { QRModal } from "../components/QRModal";
import { SiigoModal } from "../inventory/components/SiigoModal";
import { SiigoIcon } from "../inventory/components/SiigoIcon";

const PAGE_SIZE = 8;

export const ProductsPage = memo(function ProductsPage({ onNavigateUsage }: { onNavigateUsage: () => void }) {
  const { t } = useI18n();
  const { canCreateProduct, canEditProduct } = useRole();
  const { isAtLimit, isFeatureEnabled, refresh: refreshUsage } = useTenantUsage();
  const productAtLimit = isAtLimit("products");
  const bulkImportEnabled = isFeatureEnabled("bulkImport");
  const qrEnabled = isFeatureEnabled("qrBarcode");
  const [items, setItems] = useState<Product[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState<"" | ProductStatus>("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSiigo, setShowSiigo] = useState(false);
  const [editingItem, setEditingItem] = useState<Product | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [planLimitMessage, setPlanLimitMessage] = useState<string | null>(null);
  const [qrItem, setQrItem] = useState<{ code: string; name: string } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productData, unitData] = await Promise.all([
        listProducts({ page, limit: PAGE_SIZE, search: debouncedSearch || undefined, status: filter || undefined, categoryId: categoryFilter || undefined }),
        units.length === 0 ? listTypeUnits() : Promise.resolve(units),
      ]);
      setItems(productData.items);
      setMeta(productData.meta);
      if (units.length === 0) setUnits(unitData as TypeUnit[]);
    } catch {}
    setLoading(false);
  }, [page, debouncedSearch, filter, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items)).catch(() => {}); }, []);

  const showToast = useCallback((msg: string, type: "success" | "error") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getError = useCallback((code: ProductErrorCode) => {
    const map: Record<ProductErrorCode, string> = {
      DUPLICATE_CODE: t.products.errorDuplicateCode, VALIDATION: t.products.errorValidation,
      NOT_FOUND: t.products.errorForbidden, FORBIDDEN: t.products.errorForbidden,
      UNAUTHORIZED: t.products.errorForbidden, UNKNOWN: t.products.errorForbidden,
    };
    return map[code];
  }, [t.products]);

  const handleCreate = useCallback(async (data: { code: string; name: string; description?: string; typeUnitId: string; categoryId?: string }) => {
    try { await createProduct(data); showToast(t.products.successCreated, "success"); setShowForm(false); fetchData(); refreshUsage(); }
    catch (err) {
      if (isPlanLimitError(err)) { setPlanLimitMessage(err.backendMessage); return; }
      if (isProductError(err)) showToast(getError(err.message as ProductErrorCode), "error");
    }
  }, [fetchData, showToast, t.products.successCreated, getError, refreshUsage]);

  const handleUpdate = useCallback(async (data: { code: string; name: string; description?: string; typeUnitId: string; categoryId?: string }) => {
    if (!editingItem) return;
    try { await updateProduct({ id: editingItem.id, name: data.name, description: data.description ?? null }); showToast(t.products.successUpdated, "success"); setEditingItem(null); setShowForm(false); fetchData(); }
    catch (err) { if (isProductError(err)) showToast(getError(err.message as ProductErrorCode), "error"); }
  }, [editingItem, fetchData, showToast, t.products.successUpdated, getError]);

  const handleToggleStatus = useCallback(async (item: Product) => {
    const newStatus: ProductStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try { await updateProductStatus({ id: item.id, status: newStatus }); showToast(t.products.successStatusChanged, "success"); fetchData(); refreshUsage(); }
    catch (err) { if (isProductError(err)) showToast(getError(err.message as ProductErrorCode), "error"); }
  }, [fetchData, showToast, t.products.successStatusChanged, getError, refreshUsage]);

  const getUnitAbbr = useCallback((typeUnitId: string) => units.find(u => u.id === typeUnitId)?.abbreviation ?? "—", [units]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div className="platform-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.products.title} ({meta.totalItems})</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.products.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {canCreateProduct && (
            <button onClick={() => setShowSiigo(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#555", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `all ${transitions.fast}` }} onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#f9f9f9"; }} onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}>
              <SiigoIcon size={16} /> Siigo
            </button>
          )}
          {canCreateProduct && bulkImportEnabled && <button onClick={() => setShowImport(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#555", background: "#fff", border: "1px solid #e0e0e0", borderRadius: "10px", padding: "10px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
            {t.products.importProducts}
          </button>}
          {canCreateProduct && (
            <Tooltip content={t.planLimit.tooltipDisabled} disabled={!productAtLimit}>
              <button disabled={productAtLimit} onClick={() => { setShowForm(true); setEditingItem(null); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: productAtLimit ? "#ccc" : colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: productAtLimit ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}`, opacity: productAtLimit ? 0.7 : 1 }} onMouseEnter={e => { if (!productAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover; }} onMouseLeave={e => { if (!productAtLimit) (e.currentTarget as HTMLButtonElement).style.background = colors.primary; }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                {t.products.addProduct}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t.products.searchPlaceholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", minWidth: "240px", boxSizing: "border-box" }} />
        <div className="filter-buttons" style={{ display: "flex", gap: "4px" }}>
          {(["", "ACTIVE", "INACTIVE"] as const).map(key => (
            <button key={key} onClick={() => { setFilter(key); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: filter === key ? typography.weight.semibold : typography.weight.medium, padding: "8px 16px", borderRadius: "8px", border: `1px solid ${filter === key ? "#111" : "#e8e8e8"}`, background: filter === key ? "#111" : "#fff", color: filter === key ? "#fff" : "#666", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}>
              {key === "" ? t.products.filterAll : key === "ACTIVE" ? t.products.filterActive : t.products.filterInactive}
            </button>
          ))}
        </div>
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: categoryFilter ? "#111" : "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer" }}>
          <option value="">{t.products.filterAllCategories}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {activeJobId && <ImportProgressBar jobId={activeJobId} onCompleted={fetchData} />}

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.products.colCode, t.products.colName, t.products.colUnit, t.products.colCategory, t.products.colStatus, ...(canEditProduct ? [t.products.colActions] : [])].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 20px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.code}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.name}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{getUnitAbbr(item.typeUnitId)}</span></td>
                    <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{item.categoryName ?? "—"}</span></td>
                    <td style={cell}><StatusBadge status={item.status} /></td>
                    <td style={cell}>
                      {canEditProduct && <div style={{ display: "flex", gap: "6px" }}>
                        <RowBtn label={t.products.editProduct} onClick={() => { setEditingItem(item); setShowForm(true); }} />
                        <RowBtn label={item.status === "ACTIVE" ? t.products.deactivate : t.products.activate} onClick={() => handleToggleStatus(item)} danger={item.status === "ACTIVE"} />
                        {qrEnabled && <RowBtn label="QR" onClick={() => setQrItem({ code: item.code, name: item.name })} />}
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ServerPagination meta={meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {showForm && <ProductFormModal item={editingItem} onSave={editingItem ? handleUpdate : handleCreate} onClose={() => { setShowForm(false); setEditingItem(null); }} />}
      {showImport && <ProductImportModal onClose={() => setShowImport(false)} onDone={(jobId) => { setActiveJobId(jobId); setShowImport(false); }} />}
      {toast && <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: toast.type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300, animation: "fadeInUp 0.3s ease" }}>{toast.message}</div>}
      {planLimitMessage && (
        <PlanLimitModal
          message={planLimitMessage}
          onClose={() => setPlanLimitMessage(null)}
          onViewPlan={() => { setPlanLimitMessage(null); onNavigateUsage(); }}
        />
      )}
      {qrItem && (
        <QRModal code={qrItem.code} label={qrItem.name} onClose={() => setQrItem(null)} />
      )}
      {showSiigo && (
        <SiigoModal
          configs={[{ entity: "PRODUCTS", statusType: "sync_products", title: "Productos", subtitle: "Sincroniza el catálogo de productos desde Siigo." }]}
          onClose={() => setShowSiigo(false)}
        />
      )}
    </div>
  );
});

const cell: React.CSSProperties = { padding: "14px 20px", verticalAlign: "middle" };

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "ACTIVE";
  return <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)", color: isActive ? "#16a34a" : "#6b7280", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}><span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} />{isActive ? "Active" : "Inactive"}</span>;
}

function RowBtn({ label, onClick, danger = false }: { label: string; onClick: () => void; danger?: boolean }) {
  return <button onClick={onClick} style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, color: danger ? colors.error : "#666", background: "transparent", border: `1px solid ${danger ? "rgba(239,68,68,0.3)" : "#e8e8e8"}`, borderRadius: "7px", padding: "5px 10px", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}>{label}</button>;
}

function ServerPagination({ meta, onPageChange }: { meta: ProductMeta; onPageChange: (p: number) => void }) {
  if (meta.totalPages <= 1) return null;
  const start = (meta.currentPage - 1) * meta.itemsPerPage + 1;
  const end = Math.min(meta.currentPage * meta.itemsPerPage, meta.totalItems);

  const pages: (number | "...")[] = [];
  const total = meta.totalPages;
  const current = meta.currentPage;

  if (total <= 9) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    const rangeSize = 5;
    let rangeStart = Math.max(2, current - Math.floor(rangeSize / 2));
    let rangeEnd = rangeStart + rangeSize - 1;
    if (rangeEnd >= total) {
      rangeEnd = total - 1;
      rangeStart = Math.max(2, rangeEnd - rangeSize + 1);
    }

    pages.push(1);
    if (rangeStart > 2) pages.push("...");
    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
    if (rangeEnd < total - 1) pages.push("...");
    pages.push(total);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f2f2f2" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#aaa" }}>{start}–{end} of {meta.totalItems}</span>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        <PgBtn label="‹" disabled={current === 1} onClick={() => onPageChange(current - 1)} />
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`dots-${i}`} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#ccc", padding: "0 4px", userSelect: "none" }}>...</span>
          ) : (
            <PgBtn key={p} label={String(p)} active={p === current} onClick={() => onPageChange(p)} />
          )
        )}
        <PgBtn label="›" disabled={current === total} onClick={() => onPageChange(current + 1)} />
      </div>
    </div>
  );
}

function PgBtn({ label, active = false, disabled = false, onClick }: { label: string; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return <button onClick={onClick} disabled={disabled} style={{ width: "30px", height: "30px", borderRadius: "8px", border: active ? "none" : "1px solid #e8e8e8", background: active ? "#111" : "#fff", color: active ? "#fff" : disabled ? "#ddd" : "#666", fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: active ? typography.weight.bold : typography.weight.medium, cursor: disabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: disabled ? 0.5 : 1, transition: `all ${transitions.fast}` }}>{label}</button>;
}

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px", gap: "12px", textAlign: "center" }}><div style={{ width: "48px", height: "48px", background: "#f5f5f5", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg></div><p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.products.emptyTitle}</p><p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb", margin: 0, maxWidth: "300px" }}>{t.products.emptySubtitle}</p></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}
